// Offline mode: pure helpers (no React, no network, no browser globals). The offline engine, when the host app runs one,
// reports its state at GET <api base>/_oa/status; everything here only interprets that JSON.
//
// State shape (README of orderart-offline-pos, EngineState.toJson()):
//   { mode: 'online' | 'offline' | 'forced_offline', capabilities: { name: boolean }, outboxCount, parkedCount, flaggedCount,
//     lastUploadAt, lastUploadError, uploadNeedsLogin, deviceStatus: 'unregistered' | 'active' | 'revoked' | 'needs_reauth',
//     staffLoginEnabled, snapshotAgeSeconds: { domain: seconds | -1 }, warnings: ['key: text', ...] }
// With no engine the state is simply ONLINE_STATE and every helper below answers "everything is allowed, nothing to show".

export const ONLINE_STATE = Object.freeze({
  mode: 'online',
  capabilities: Object.freeze({}),
  outboxCount: 0,
  parkedCount: 0,
  flaggedCount: 0,
  lastUploadAt: null,
  lastUploadError: null,
  uploadNeedsLogin: false,
  deviceStatus: undefined,
  staffLoginEnabled: undefined,
  snapshotAgeSeconds: Object.freeze({}),
  warnings: Object.freeze([]),
});

const MODES = new Set(['online', 'offline', 'forced_offline']);
const DEVICE_STATUSES = new Set(['unregistered', 'active', 'revoked', 'needs_reauth']);
const count = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.floor(Number(v)) : 0);

/** Anything the engine sends is clamped to a safe shape: an unknown mode means online, never a half-offline page. */
export function normalizeState(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return ONLINE_STATE;
  const capabilities = {};
  Object.entries(raw.capabilities && typeof raw.capabilities === 'object' ? raw.capabilities : {}).forEach(([k, v]) => {
    capabilities[k] = v === true;
  });
  const ages = {};
  Object.entries(raw.snapshotAgeSeconds && typeof raw.snapshotAgeSeconds === 'object' ? raw.snapshotAgeSeconds : {}).forEach(([k, v]) => {
    if (Number.isFinite(Number(v))) ages[k] = Number(v);
  });
  const lastUploadAt = Number(raw.lastUploadAt);
  return {
    mode: MODES.has(raw.mode) ? raw.mode : 'online',
    capabilities,
    outboxCount: count(raw.outboxCount),
    parkedCount: count(raw.parkedCount),
    flaggedCount: count(raw.flaggedCount),
    lastUploadAt: Number.isFinite(lastUploadAt) && lastUploadAt > 0 ? lastUploadAt : null,
    lastUploadError: typeof raw.lastUploadError === 'string' && raw.lastUploadError ? raw.lastUploadError : null,
    uploadNeedsLogin: raw.uploadNeedsLogin === true,
    deviceStatus: DEVICE_STATUSES.has(raw.deviceStatus) ? raw.deviceStatus : undefined,
    staffLoginEnabled: typeof raw.staffLoginEnabled === 'boolean' ? raw.staffLoginEnabled : undefined,
    snapshotAgeSeconds: ages,
    warnings: Array.isArray(raw.warnings) ? raw.warnings.filter((w) => typeof w === 'string') : [],
  };
}

/** The extra parts of GET /_oa/status the Offline page shows: { device: {...}, staff: {...} }. Counts and ages only, never a secret. */
export function normalizeExtras(raw) {
  const device = raw && typeof raw.device === 'object' && raw.device ? raw.device : {};
  const staff = raw && typeof raw.staff === 'object' && raw.staff ? raw.staff : {};
  return {
    device: {
      status: DEVICE_STATUSES.has(device.status) ? device.status : undefined,
      name: typeof device.name === 'string' && device.name ? device.name : null,
      lastStaffRefreshAt: Number(device.lastStaffRefreshAt) > 0 ? Number(device.lastStaffRefreshAt) : null,
      lastError: typeof device.lastError === 'string' && device.lastError ? device.lastError : null,
    },
    staff: {
      credentials: count(staff.credentials),
      ageSeconds: Number.isFinite(Number(staff.ageSeconds)) ? Number(staff.ageSeconds) : null,
      maxAgeSeconds: Number.isFinite(Number(staff.maxAgeSeconds)) ? Number(staff.maxAgeSeconds) : null,
      source: typeof staff.source === 'string' ? staff.source : '',
    },
  };
}

/** Does this JSON look like the engine's status? (A cloud 404 body, an HTML page or an old engine's 501 must not.) */
export const looksLikeEngineStatus = (raw) => Boolean(raw) && typeof raw === 'object' && MODES.has(raw.mode) && typeof raw.capabilities === 'object';

export const isOfflineState = (state) => Boolean(state) && state.mode !== 'online';

/** Online: everything is allowed. Offline: only what the engine lists as true (an unlisted capability is off). */
export const canDo = (state, capability) => !isOfflineState(state) || state.capabilities?.[capability] === true;

/** Staff login is on, the PIN pad needs the engine's credentials, and the engine has none (never registered, revoked, or the credential expired): the device has to be registered again. */
export const pinNeedsRegistration = (state) =>
  state?.staffLoginEnabled === true && state?.capabilities?.staffPin !== true && state?.deviceStatus !== 'active';

/**
 * The staff screen lock may only be up while it can be taken down again. Online the cloud checks the PIN, so always yes. Offline the
 * engine checks it from the credentials on the device, which exist only while capability `staffPin` is true; without them nobody could
 * unlock the till, so the lock must not engage (and one already up is released).
 */
export const staffLockAllowed = (state) => !isOfflineState(state) || state.capabilities?.staffPin === true;

/** Text before the colon of an engine warning ("key: text"): { key, text }. */
export function warningParts(w) {
  const s = String(w || '');
  const i = s.indexOf(': ');
  return i > 0 && /^[a-z_]+$/.test(s.slice(0, i)) ? { key: s.slice(0, i), text: s.slice(i + 2) } : { key: '', text: s };
}

/** The first warning with this key (e.g. 'surcharges_not_downloaded'), as { key, text }, or null. */
export const findWarning = (state, key) => (state?.warnings || []).map(warningParts).find((w) => w.key === key) || null;
